class TJMobileCollectionFilters extends HTMLElement {
  connectedCallback() {
    this.events = new AbortController()
    const options = { signal: this.events.signal }
    this.form = this.querySelector('form')
    this.price = this.querySelector('tj-collection-price-filter')
    this.button = this.querySelector('[data-sheet-results]')
    this.error = this.querySelector('[data-sheet-error]')
    this.addEventListener('sheet:open', () => {
      this.form.reset()
      this.form.querySelectorAll('[data-sheet-extra-filter]').forEach(input => { input.disabled = false })
      const params = new URLSearchParams(location.search)
      this.form.querySelectorAll('input[type=checkbox], input[type=radio]').forEach(input => {
        const values = input.hasAttribute('data-metal-values') ? input.value.split(',') : [input.value]
        input.checked = values.some(value => params.getAll(input.name).includes(value))
      })
      if (this.price?.bounds) {
        this.price.dirty = false
        this.price.applyBounds(this.price.bounds)
      }
      this.updateCount()
    }, options)
    this.querySelector('[data-collection-sheet-reset]').addEventListener('click', () => {
      this.form.querySelectorAll('input[type=checkbox], input[type=radio]').forEach(input => { input.checked = false })
      this.form.querySelectorAll('[data-sheet-extra-filter]').forEach(input => { input.disabled = true })
      this.price?.setDefaultValues()
      this.form.requestSubmit()
    }, options)
    this.form.addEventListener('click', event => {
      if (!event.target.closest('[data-ring-shapes-next]')) return
      const row = this.form.querySelector('[data-ring-shapes]')
      if (!row) return
      row.scrollTo({ left: row.scrollLeft >= row.scrollWidth - row.clientWidth - 2 ? 0 : row.scrollLeft + row.clientWidth + 12, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' })
    }, options)
    this.form.addEventListener('input', () => this.scheduleCount(), { ...options, capture: true })
    this.form.addEventListener('change', () => this.scheduleCount(), { ...options, capture: true })
    this.form.addEventListener('submit', event => {
      event.preventDefault()
      this.price?.syncSliders(undefined, true)
      this.button.setAttribute('aria-busy', 'true')
      location.assign(this.formUrl().href)
    }, options)
    queueMicrotask(() => {
      if (!this.isConnected) return
      const params = new URLSearchParams(location.search)
      const selected = [...this.form.querySelectorAll('input[type=checkbox], input[type=radio]')].filter(input => {
        const values = input.hasAttribute('data-metal-values') ? input.value.split(',') : [input.value]
        return values.some(value => params.getAll(input.name).includes(value))
      }).length
      const priceCount = ['filter.v.price.gte', 'filter.v.price.lte'].some(key => params.has(key)) ? 1 : 0
      this.querySelector('[data-sheet-count]').textContent = selected + priceCount ? `(${selected + priceCount})` : ''
    })
  }
  disconnectedCallback() {
    this.events?.abort()
    this.countRequest?.abort()
    clearTimeout(this.countTimer)
  }
  formUrl() {
    const url = new URL(location.href)
    for (const key of [...url.searchParams.keys()]) {
      if (key.startsWith('filter.') || key === 'page' || key === 'section_id') url.searchParams.delete(key)
    }
    for (const [key, value] of new FormData(this.form)) {
      if (value !== '') url.searchParams.append(key, value)
    }
    return url
  }
  scheduleCount() {
    this.countRequest?.abort()
    clearTimeout(this.countTimer)
    this.countTimer = setTimeout(() => this.updateCount(), 250)
  }
  async updateCount() {
    this.countRequest?.abort()
    const request = new AbortController()
    this.countRequest = request
    this.button.setAttribute('aria-busy', 'true')
    this.button.textContent = `${this.dataset.resultsLabel} (…)`
    this.error.hidden = true
    const source = this.formUrl()
    const minimum = Math.round(Number(source.searchParams.get('filter.v.price.gte') || 0) * 100)
    const maxValue = source.searchParams.get('filter.v.price.lte')
    const maximum = maxValue === null ? Infinity : Math.round(Number(maxValue) * 100)
    const hasPrice = source.searchParams.has('filter.v.price.gte') || maxValue !== null
    const matches = new Set()
    const seen = new Set()
    let next = source
    try {
      let count = 0
      while (next) {
        if (next.origin !== source.origin || next.pathname !== source.pathname || seen.has(next.href)) throw new Error('Invalid result page')
        seen.add(next.href)
        const ringStyle = this.dataset.ringStyle === 'true'
        if (ringStyle && this.dataset.nativePrice === 'false') {
          next.searchParams.delete('filter.v.price.gte')
          next.searchParams.delete('filter.v.price.lte')
        }
        next.searchParams.set('section_id', ringStyle ? 'tj-rings-filter-results' : 'tj-collection-filter-count')
        const response = await fetch(next, { signal: request.signal })
        if (!response.ok) throw new Error('Unable to count results')
        const html = new DOMParser().parseFromString(await response.text(), 'text/html')
        if (ringStyle) {
          const results = html.querySelector('[data-ring-results]')
          if (!results) throw new Error('Ring results unavailable')
          if (this.dataset.nativePrice === 'true' || !hasPrice) { count = Number(results.dataset.total); break }
          for (const card of results.querySelector('[data-ring-result-cards]').content.querySelectorAll('[data-ring-card]')) {
            if (card.dataset.priceValues.split(',').some(value => Number(value) >= minimum && Number(value) <= maximum)) matches.add(card.dataset.productId)
          }
          count = matches.size
          next = results.dataset.next ? new URL(results.dataset.next, source) : null
          continue
        }
        const data = JSON.parse(html.querySelector('[data-sheet-result-count]').textContent)
        if (data.nativePrice || !hasPrice) { count = data.count; break }
        for (const product of data.products) {
          if (product.variants.some(variant => variant.matched !== false && variant.price >= minimum && variant.price <= maximum)) matches.add(product.id)
        }
        count = matches.size
        next = data.next ? new URL(data.next, source) : null
      }
      if (request.signal.aborted || !this.isConnected) return
      this.button.textContent = `${this.dataset.resultsLabel} (${count.toLocaleString()})`
    } catch (error) {
      if (error.name !== 'AbortError' && !request.signal.aborted && this.isConnected) {
        this.button.textContent = this.dataset.resultsLabel
        this.error.hidden = false
      }
    } finally {
      if (this.countRequest === request) this.button.removeAttribute('aria-busy')
    }
  }
}
if (!customElements.get('tj-mobile-collection-filters')) customElements.define('tj-mobile-collection-filters', TJMobileCollectionFilters)
